/**
 * O PAYLOAD do cartão de confirmação (`crm/confirmacao`) — fonte ÚNICA.
 *
 * Duas origens montam o mesmo cartão e precisam produzir o MESMO shape: o
 * evento `tool-approval-request` (ao vivo) e o snapshot `GET /conversas/{id}/
 * run-atual` (reidratação, A-147). Enquanto a montagem vivia só dentro de
 * `apiChat.js`, a segunda origem inventou um formato próprio — pôs os campos
 * dentro de `preview` — e o `validar.js` do registry reprovou com
 * `props.resumo: required` … `props.operacao_label: required`: na tela isso é
 * "Não consegui montar este cartão · dados fora do contrato", exatamente o
 * sintoma que o A-147 existe para acabar. Medido em 19/09/2026 contra o
 * payload REAL do run `b592d381`.
 *
 * ⚠️ Os rótulos NÃO podem sair como string vazia: o schema
 * (`ui-registry/schemas/ui-crm-confirmacao.schema.json`) pede `minLength: 1`
 * em `entidade_label`/`operacao_label`, então `''` reprova igual a ausente —
 * foi o segundo vermelho do teste de junção, depois que os campos subiram
 * para o topo. A ordem das fontes é a que o próprio schema declara:
 *
 *   1. o rótulo que o BACKEND resolveu (topo do snapshot, ou dentro de
 *      `preview` no evento) — `rotulos_do_preview` do `write_audit_engine`;
 *   2. o CONTRATO copiado (`ui-registry/contratos/entidade-labels.json`,
 *      byte a byte com o backend), que o schema chama de fallback e que o
 *      `ia-confirm-action.vue` já usa para montar o título.
 *
 * Nunca uma terceira lista redigitada aqui: "redigitar rótulo no front foi o
 * que fez 13 das 16 entidades aparecerem como 'Registro'" (descrição do campo
 * `entidade_label` no schema).
 */
import CONTRATO_DE_ROTULOS from '@modules/aawz-intelligence-v2/ui-registry/contratos/entidade-labels.json'

// Último recurso, quando nem o backend nem o contrato conhecem a chave: o
// MESMO texto que o cartão já mostra no título (`ia-confirm-action.vue`), para
// a tela não dizer duas coisas sobre a mesma proposta.
export const ENTIDADE_LABEL_PADRAO = 'Registro'
export const OPERACAO_LABEL_PADRAO = 'Confirmar'

function rotuloDaEntidade(doBackend, entidade) {
  return doBackend || CONTRATO_DE_ROTULOS.entidades[entidade] || ENTIDADE_LABEL_PADRAO
}

function rotuloDaOperacao(doBackend, operacao) {
  return doBackend || CONTRATO_DE_ROTULOS.operacoes[operacao] || OPERACAO_LABEL_PADRAO
}

/**
 * @param {Object} dados `data` do evento `tool-approval-request` OU uma
 *   entrada de `propostas_abertas` do snapshot — os dois trazem
 *   `resumo`/`entidade`/`operacao` no topo; os rótulos vêm no topo (snapshot,
 *   A-147) ou dentro de `preview` (evento).
 * @returns {Object|null} o `value` do cartão, ou `null` quando não há nada
 *   que desenhar (o chamador então não cria parte nenhuma).
 */
// O snapshot traz o campo no TOPO; o evento, dentro de `preview`. Um leitor
// só para os dois evita repetir `(dados && dados.x) || preview.x` cinco vezes
// (o que levava a função a complexidade 19 no ESLint).
function campo(dados, preview, nome) {
  return (dados && dados[nome]) || preview[nome] || ''
}

export function valorSinteticoDaProposta(dados) {
  const preview = (dados && dados.preview) || {}
  const resumo = campo(dados, preview, 'resumo')
  const entidade = campo(dados, preview, 'entidade')
  const operacao = campo(dados, preview, 'operacao')
  if (!resumo && !entidade && !operacao) return null
  const entidadeLabel = campo(dados, preview, 'entidade_label')
  const operacaoLabel = campo(dados, preview, 'operacao_label')
  return {
    requer_confirmacao: true,
    resumo: resumo,
    entidade: entidade,
    operacao: operacao,
    entidade_label: rotuloDaEntidade(entidadeLabel, entidade),
    operacao_label: rotuloDaOperacao(operacaoLabel, operacao),
  }
}

/**
 * ★ [#213] O `value` REAL da tool vence o sintético: desde então o evento
 * `confirmacao` do Modo Agente traz o `value` do passo de PREVIEW quando a
 * tool tem cartão próprio (`fee-fixo/simulacao-card` e as outras cinco de
 * two-step próprio) — o sintético não tem `inputs` nem `resultado` e
 * reprovaria no schema DELAS. O sintético continua servindo o cartão
 * GENÉRICO das write-tools do WriteAuditEngine, cujo evento nunca carrega
 * `value` (nem deve — propor a escrita não pode executá-la).
 */
export function valorDaPropostaParaOCartao(dados) {
  if (!dados) return null
  if (dados.value && typeof dados.value === 'object') return dados.value
  return valorSinteticoDaProposta(dados)
}
